import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Toast notification types
 */
export type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * Toast notification interface
 */
export interface Toast {
    id: string;
    type: ToastType;
    title: string;
    message?: string;
    duration?: number;
}

/**
 * UI state interface
 */
interface UIState {
    theme: 'light' | 'dark' | 'system';
    sidebarOpen: boolean;
    mobileMenuOpen: boolean;
    toasts: Toast[];
    isLoading: boolean;
    loadingMessage: string | null;
    isCartOpen: boolean;
}

/**
 * Get initial theme from localStorage or system preference
 */
const getInitialTheme = (): 'light' | 'dark' | 'system' => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;

    if (savedTheme) {
        return savedTheme;
    }

    return 'system';
};

const initialState: UIState = {
    theme: getInitialTheme(),
    sidebarOpen: true,
    mobileMenuOpen: false,
    toasts: [],
    isLoading: false,
    loadingMessage: null,
    isCartOpen: false,
};

/**
 * UI slice for global UI state management
 */
const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        /**
         * Set theme
         */
        setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
            state.theme = action.payload;
            localStorage.setItem('theme', action.payload);

            // Apply theme to document
            const root = document.documentElement;
            root.classList.remove('light', 'dark');

            if (action.payload === 'system') {
                const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
                    ? 'dark'
                    : 'light';
                root.classList.add(systemTheme);
            } else {
                root.classList.add(action.payload);
            }
        },

        /**
         * Toggle sidebar
         */
        toggleSidebar: (state) => {
            state.sidebarOpen = !state.sidebarOpen;
        },

        /**
         * Set sidebar state
         */
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.sidebarOpen = action.payload;
        },

        /**
         * Toggle mobile menu
         */
        toggleMobileMenu: (state) => {
            state.mobileMenuOpen = !state.mobileMenuOpen;
        },

        /**
         * Set mobile menu state
         */
        setMobileMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.mobileMenuOpen = action.payload;
        },

        /**
         * Add toast notification
         */
        addToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
            const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            state.toasts.push({
                ...action.payload,
                id,
                duration: action.payload.duration ?? 5000,
            });
        },

        /**
         * Remove toast notification
         */
        removeToast: (state, action: PayloadAction<string>) => {
            state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
        },

        /**
         * Clear all toasts
         */
        clearToasts: (state) => {
            state.toasts = [];
        },

        /**
         * Set global loading state
         */
        setLoading: (
            state,
            action: PayloadAction<{ isLoading: boolean; message?: string }>
        ) => {
            state.isLoading = action.payload.isLoading;
            state.loadingMessage = action.payload.message ?? null;
        },

        /**
         * Set cart open state
         */
        setCartOpen: (state, action: PayloadAction<boolean>) => {
            state.isCartOpen = action.payload;
        },

        /**
         * Toggle cart open state
         */
        toggleCart: (state) => {
            state.isCartOpen = !state.isCartOpen;
        },
    },
});

export const {
    setTheme,
    toggleSidebar,
    setSidebarOpen,
    toggleMobileMenu,
    setMobileMenuOpen,
    addToast,
    removeToast,
    clearToasts,
    setLoading,
    setCartOpen,
    toggleCart,
} = uiSlice.actions;

export default uiSlice.reducer;
